/**
 * Reserve a Resource for an Account — Resource Detail ▸ Reservation
 * (docs/specs/resource-reservation.md).
 *
 * A reservation sets aside the Resource's open time (NOS capacity minus NOS guidance) for one
 * Account through an optional end date. It is a flag on the Resource, not an Assignment:
 * nothing is staffed, no points move, and nothing is written back to NOS. RM/ADMIN only —
 * the server enforces it; callers render this only for those roles.
 *
 * Re-opening on a reserved Resource edits the reservation in place (same PUT). Release lives on
 * the profile section rather than here, so "change" and "end" stay separate choices.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAccounts } from "@/hooks/use-accounts";
import { type ResourceReservationView, useReserveResource } from "@/hooks/use-resource-reservation";
import { isoDay } from "@/lib/resource-reservation";

function formSchema(today: string) {
  return z.object({
    accountId: z.string().min(1, "Choose an Account"),
    /** `YYYY-MM-DD` from the date input, or "" for open-ended. */
    until: z
      .string()
      .refine((v) => v === "" || v >= today, { message: "The end date is in the past" }),
    note: z.string().max(500, { message: "Keep the note under 500 characters" }),
  });
}

type FormValues = z.infer<ReturnType<typeof formSchema>>;

function valuesFor(reservation: ResourceReservationView | null): FormValues {
  return {
    accountId: reservation?.accountId ?? "",
    // An expired reservation is being renewed — start from a blank end date, not a past one.
    until: reservation?.active && reservation.until !== null ? reservation.until : "",
    note: reservation?.note ?? "",
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: { id: string; name: string; reservation: ResourceReservationView | null };
}

export function ReserveResourceDialog({ open, onOpenChange, resource }: Props) {
  const today = isoDay(new Date());
  const schema = useMemo(() => formSchema(today), [today]);
  const reserve = useReserveResource(resource.id);
  const accounts = useAccounts({ limit: 100 });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: valuesFor(resource.reservation),
  });

  useEffect(() => {
    if (!open) return;
    form.reset(valuesFor(resource.reservation));
  }, [open, resource.reservation, form]);

  const options = useMemo(
    () => (accounts.data?.data ?? []).map((a) => ({ value: a.id, label: a.name })),
    [accounts.data],
  );

  async function onSubmit(values: FormValues) {
    await reserve.mutateAsync({
      accountId: values.accountId,
      until: values.until === "" ? null : values.until,
      note: values.note.trim() === "" ? null : values.note.trim(),
    });
    onOpenChange(false);
  }

  const editing = resource.reservation?.active === true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Change reservation" : "Reserve for an Account"} — {resource.name}
          </DialogTitle>
          <DialogDescription>
            Reserves {resource.name}&rsquo;s open time — capacity not already in NOS guidance — for
            one Account. Other Accounts&rsquo; Solutions and plans see that time as taken. Nothing
            is assigned, and NOS is not changed.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          {/* handleSubmit swallows the rejection; the mutation toasts its own error and the
              dialog stays open. */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={options}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Find an Account…"
                      emptyMessage="No matching Account"
                      loading={accounts.isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="until"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reserved until</FormLabel>
                  <FormControl>
                    <Input type="date" min={today} {...field} />
                  </FormControl>
                  <FormDescription>
                    Optional, and inclusive. Leave blank to keep the reservation until released.
                    After this date the Resource reads as available everywhere.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="Optional — why they are reserved" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={reserve.isPending}>
                {reserve.isPending ? "Saving…" : editing ? "Save reservation" : "Reserve"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
